import { Chip } from "@/components/ui/chip";
import { MUI_TOOLTIP_DELAY_MS, MuiTooltip } from "@/components/ui/mui-tooltip";
import { MuiTypography } from "@/components/ui/mui-typography";
import type { CatalogVariantChipRow } from "@/lib/catalog-item-variants";

// The item details page's two variant rows — the donor's `FinishVariants.tsx` and
// `DimensionVariants.tsx`, side by side in `Metadata/`.
//
// They read as one component written twice, and they are not. The Color row
// clamps every chip to a third of the row and hangs a tooltip off each one; the
// Size row does neither. Both differences are in the donor's `sx` and both earn
// their keep: a finish name ("Brushed Nickel with Antique Bronze Accents") needs
// truncating, and `30.0W x 18.5D` never does. Folding the two into one component
// with a `clamp` flag would say they are the same row with a setting, when what
// is actually true is that they are two rows about two different kinds of string.
//
// Neither component decides anything: which chips exist, what they say and which
// one is current all arrive already worked out from `@/lib/catalog-item-variants`,
// under unit test there. What is left here is arrangement, which is why these are
// browser-tested for geometry and nothing else.

interface VariantChipRowProps {
  /**
   * The row to draw, or `null` to draw nothing.
   *
   * `null` rather than a `hidden` flag because the gate and the contents are one
   * decision in the donor — the row is hidden precisely when the *displayed*
   * variant has nothing to print — and `finishVariantRow`/`dimensionVariantRow`
   * return exactly that.
   */
  row: CatalogVariantChipRow | null;
  /** Called with the item id the clicked chip switches to. */
  onSelect: (itemId: number) => void;
}

/** The row's label is the shared `body1`; this is only the donor's own `sx` on
 * it — `mb: 0.875`, 7px rather than one of MUI's usual 8px steps, so it is
 * spelled out rather than rounded — and the long value wrapping. */
const HEADING = "mb-[7px] break-words";

/**
 * The chip row itself.
 *
 * `mr`/`mb` margins on each chip rather than a flex `gap`, because the Color
 * row's `maxWidth: calc(33% - 8px)` is written *against* the 8px right margin —
 * that subtraction is what lets three chips share a row. A `gap` would change
 * the arithmetic and also drop the trailing space after the last chip, which the
 * donor has.
 */
const CHIP_MARGINS = "mr-2 mb-2";

/**
 * The `Color: {finish}` row.
 *
 * `width: 100%` on the wrapper is the donor's, and commented there as "ensure
 * takes up full available width" — it is what the chips' `33%` is a third *of*.
 */
export function ItemFinishVariants({ row, onSelect }: VariantChipRowProps) {
  if (!row) return null;

  return (
    <div>
      <MuiTypography data-testid="item-color" variant="body1" className={HEADING}>
        Color: {row.current}
      </MuiTypography>
      <div className="mb-2 flex w-full flex-wrap">
        {row.chips.map((chip) => (
          // `MUI_TOOLTIP_DELAY_MS`: this row is the one place the donor reaches
          // for `@mui/material/Tooltip` directly (`FinishVariants.tsx:3`), so it
          // waits MUI's 100ms rather than the react-ui wrapper's nothing.
          //
          // `asChild`, so the trigger *is* the chip: a `<button>` nested inside
          // the trigger's own button would be invalid markup and would leave the
          // chip unreachable by its accessible name.
          //
          // The title is unconditional, as the donor's is — not "only when it
          // overflows".
          <MuiTooltip
            key={chip.itemId}
            title={chip.label}
            side="top"
            delayMs={MUI_TOOLTIP_DELAY_MS}
            asChild
          >
            <Chip
              data-testid={`variant-finish-${chip.itemId}`}
              color={chip.isCurrent ? "primary" : "default"}
              label={chip.label}
              onClick={() => onSelect(chip.itemId)}
              // `minWidth: '120px'` is a floor, not a width: it keeps a row of
              // short finish names from reading as a ragged pile of pills.
              //
              // `overflow-hidden` is this call site's, as it is the donor's
              // (`FinishVariants.tsx:99`): MUI's `ChipRoot` sets no overflow —
              // only its label does — and the clamp above is what makes this
              // one chip need it.
              className={`${CHIP_MARGINS} max-w-[calc(33%_-_8px)] min-w-[120px] overflow-hidden`}
            />
          </MuiTooltip>
        ))}
      </div>
    </div>
  );
}

/**
 * The `Size: {dimensions}` row.
 *
 * No `width: 100%`, no clamp, no tooltip — the donor's `sx` here is `{ mr: 1,
 * mb: 1 }` and nothing else.
 */
export function ItemDimensionVariants({ row, onSelect }: VariantChipRowProps) {
  if (!row) return null;

  return (
    <div>
      <MuiTypography data-testid="item-size" variant="body1" className={HEADING}>
        Size: {row.current}
      </MuiTypography>
      <div className="mb-2 flex flex-wrap">
        {row.chips.map((chip) => (
          <Chip
            key={chip.itemId}
            data-testid={`variant-dimensions-${chip.itemId}`}
            color={chip.isCurrent ? "primary" : "default"}
            label={chip.label}
            onClick={() => onSelect(chip.itemId)}
            className={CHIP_MARGINS}
          />
        ))}
      </div>
    </div>
  );
}
